import * as React from "react";

/**
 * Phase E1.f — persist a user's chosen rows-per-page across reloads.
 *
 * Each `<DataTablePagination>` consumer passes a stable string `scope`
 * (e.g. `"students"`, `"staff"`, `"admissions-workload"`). The chosen
 * value is mirrored into `localStorage` under
 * `eduviaos:pageSize:<scope>` so a user's preference for 100 rows on
 * the Students tab does NOT leak into Staff (each list has its own
 * preferred density).
 *
 * Why a hook + not a `<PaginationContext>`?
 *   - The five toolbar pages are siblings, not a tree. No shared
 *     ancestor short of `<App>` and there's no other state worth
 *     hoisting that high.
 *   - localStorage is the single source of truth. The hook is a thin
 *     glue layer that keeps React state in sync.
 *
 * Resilience choices:
 *   - SSR safety: reads from `localStorage` only inside a useEffect
 *     so the initial server render (none today, but if Vite SSR
 *     lands later) doesn't crash.
 *   - Parse safety: NaN/zero/negative storage values fall back to
 *     `defaultPageSize`.
 *   - Allowed-list guard: an attacker who hand-edits localStorage to
 *     a 10,000 row pageSize would otherwise blow up the request
 *     limit. We clamp to `allowedValues` (defaults to the canonical
 *     Iron Rule #16 set [10, 25, 50, 100]).
 *   - Write failures: localStorage can throw QuotaExceededError or
 *     SecurityError in private-browsing modes. We swallow + degrade
 *     gracefully — the runtime state still updates, the persistence
 *     just doesn't stick.
 */

const STORAGE_PREFIX = "eduviaos:pageSize:";

const DEFAULT_ALLOWED_VALUES = [10, 25, 50, 100] as const;

interface UsePageSizePreferenceOptions {
  scope: string;
  defaultPageSize: number;
  allowedValues?: readonly number[];
}

export function usePageSizePreference({
  scope,
  defaultPageSize,
  allowedValues = DEFAULT_ALLOWED_VALUES,
}: UsePageSizePreferenceOptions): [number, (next: number) => void] {
  const allowedSet = React.useMemo(() => new Set(allowedValues), [allowedValues]);
  const storageKey = `${STORAGE_PREFIX}${scope}`;

  // Read once on mount. We don't subscribe to `storage` events: each
  // tab's preference is independent — a Staff power-user with two
  // tabs open might genuinely want one at 25 and another at 100.
  const [pageSize, setPageSizeState] = React.useState<number>(() => {
    if (typeof window === "undefined") return defaultPageSize;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) return defaultPageSize;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed <= 0) return defaultPageSize;
      if (!allowedSet.has(parsed)) return defaultPageSize;
      return parsed;
    } catch {
      // localStorage can throw in cookie-blocked private windows; the
      // user still gets a working table, just no persistence.
      return defaultPageSize;
    }
  });

  const setPageSize = React.useCallback(
    (next: number) => {
      // Defensive clamp: a caller could plumb a custom pageSizeOptions
      // list into `<DataTablePagination>` that diverges from the
      // allowed set we persist. Drop the write rather than corrupt the
      // stored value with something we'd refuse on re-read.
      if (!allowedSet.has(next)) {
        setPageSizeState(next);
        return;
      }
      setPageSizeState(next);
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(storageKey, String(next));
        }
      } catch {
        // Silent — see hook header.
      }
    },
    [allowedSet, storageKey],
  );

  return [pageSize, setPageSize];
}
